import { useState } from 'react'
import { useEncouragements } from '../hooks/useData'
import { scanTranscript, generateEncouragementSummary } from '../hooks/useSermons'
import { useAuth } from '../hooks/useAuth'
import { parseBibleRefs } from '../lib/bibleRefs'

const DEFAULT_CLIP_S = 90
const MAX_CLIP_S = 300 // docs/FIX-ENCOURAGEMENT-UPLOAD.md — a hard 5-minute ceiling

const ACCENT = 'var(--gold)'
const s = {
  wrap:     { padding:'16px', fontFamily:"'Plus Jakarta Sans',sans-serif" },
  title:    { fontSize:18, fontWeight:800, color:'var(--ink)', marginBottom:4 },
  sub:      { fontSize:13, color:'var(--stone)', marginBottom:20 },
  label:    { fontSize:11, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--stone)', marginBottom:6, display:'block', marginTop:14 },
  input:    { width:'100%', background:'var(--white)', border:'1px solid var(--border)', borderRadius:10, padding:'11px 14px', color:'var(--ink)', fontSize:14, fontFamily:"'Plus Jakarta Sans',sans-serif", boxSizing:'border-box', outline:'none' },
  textarea: { width:'100%', background:'var(--white)', border:'1px solid var(--border)', borderRadius:10, padding:'11px 14px', color:'var(--ink)', fontSize:13, fontFamily:"'Plus Jakarta Sans',sans-serif", boxSizing:'border-box', outline:'none', resize:'vertical', lineHeight:1.6 },
  scanBtn:  { width:'100%', background:'rgba(232,200,122,0.12)', border:'1px solid rgba(232,200,122,0.3)', borderRadius:10, padding:'13px', fontSize:14, fontWeight:700, color:'var(--gold-ink)', cursor:'pointer', marginTop:8, fontFamily:"'Plus Jakarta Sans',sans-serif" },
  saveBtn:  { width:'100%', background:ACCENT, color:'var(--ink)', border:'none', borderRadius:10, padding:'13px', fontSize:14, fontWeight:800, cursor:'pointer', marginTop:14, fontFamily:"'Plus Jakarta Sans',sans-serif" },
  disBtn:   { opacity:0.4, cursor:'default' },
  refPill:  { display:'inline-flex', alignItems:'center', gap:6, background:'rgba(232,200,122,0.1)', border:'1px solid rgba(232,200,122,0.25)', borderRadius:18, padding:'5px 10px 5px 12px', fontSize:12, fontWeight:700, color:'var(--gold-ink)' },
  refX:     { background:'none', border:'none', color:'var(--gold-ink)', cursor:'pointer', fontSize:14, padding:0, lineHeight:1 },
  status:   { borderRadius:10, padding:'12px 14px', fontSize:13, lineHeight:1.5, marginTop:12 },
  ok:       { background:'rgba(80,200,150,0.1)', border:'1px solid rgba(80,200,150,0.25)', color:'#1F7A52' },
  err:      { background:'rgba(249,112,102,0.1)', border:'1px solid rgba(249,112,102,0.25)', color:'#C8372A' },
  info:     { background:'rgba(232,200,122,0.08)', border:'1px solid rgba(232,200,122,0.2)', color:'var(--gold-ink)' },
}

function todayIso() { return new Date().toISOString().slice(0,10) }

// Pins a YouTube link to a specific moment — "whole video" is just leaving
// this blank, no separate toggle needed. Accepts MM:SS or H:MM:SS. Strips any
// existing t= param first so re-editing an already-stamped link (saved by
// this same feature) doesn't double up.
function appendYoutubeTimestamp(url, mmss) {
  if (!url) return url
  const stripped = url.replace(/([?&])t=\d+s?(&|$)/i, (_, lead, trail) => (trail === '&' ? lead : '')).replace(/[?&]$/, '')
  const seconds = parseTimestampToSeconds(mmss)
  if (seconds == null) return stripped
  const sep = stripped.includes('?') ? '&' : '?'
  return `${stripped}${sep}t=${seconds}s`
}

// "MM:SS" or "H:MM:SS" -> whole seconds, or null if blank/unparseable.
function parseTimestampToSeconds(mmss) {
  if (!mmss) return null
  const parts = mmss.split(':').map(p => parseInt(p, 10))
  if (parts.some(Number.isNaN) || parts.length < 2 || parts.length > 3) return null
  const seconds = parts.length === 3
    ? parts[0] * 3600 + parts[1] * 60 + parts[2]
    : parts[0] * 60 + parts[1]
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null
}

function youtubeIdFromUrl(url) {
  const m = String(url || '').match(/(?:v=|youtu\.be\/|\/embed\/)([a-zA-Z0-9_-]{11})/)
  return m ? m[1] : null
}

export default function EncouragementUpload({ onSaved, churchId, existing }) {
  const { profile } = useAuth()
  const { addEncouragement, updateEncouragement } = useEncouragements(churchId)

  const isEdit = !!existing?.id
  const defaultSharedBy = profile?.full_name || ''
  const [title,       setTitle]       = useState(existing?.title || '')
  const [sharedBy,    setSharedBy]    = useState(existing?.sharedBy || defaultSharedBy)
  const [date,        setDate]        = useState(existing?.eventDate || existing?.created_at?.slice(0,10) || todayIso())
  const [text,        setText]        = useState(existing?.text || '')
  const [refs,        setRefs]        = useState(
    (existing?.refs || []).map(r => ({
      verseRef: r.verseRef, book: r.book, chapter: r.chapter, verse: r.verse,
      timestamp: r.timestamp, timestampS: r.timestampS, notes: r.notes,
    }))
  )
  const [youtubeUrl,  setYoutubeUrl]  = useState(existing?.link || '')
  const [startsAt,    setStartsAt]    = useState('')
  const [clipLength,  setClipLength] = useState(String(DEFAULT_CLIP_S))
  const [summary,     setSummary]    = useState(existing?.summary || '')

  const [scanning,   setScanning]   = useState(false)
  const [scanError,  setScanError]  = useState('')
  const [generating, setGenerating] = useState(false)
  const [genError,   setGenError]   = useState('')
  const [isLikelySermon, setIsLikelySermon] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [saving,     setSaving]     = useState(false)
  const [saved,      setSaved]      = useState(false)
  const [saveError,  setSaveError]  = useState('')
  const [manualRef,  setManualRef]  = useState('')
  const [manualErr,  setManualErr]  = useState('')

  const clipSeconds = Math.max(1, parseInt(clipLength, 10) || DEFAULT_CLIP_S)
  const clipTooLong = clipSeconds > MAX_CLIP_S

  function addManualRef() {
    setManualErr('')
    const raw = manualRef.trim()
    if (!raw) return
    // Reuse the same text-scanning regex so book aliases (Psalm/Psalms,
    // Song of Songs, etc.) and ranges work the same way as auto-detect.
    const parsed = parseBibleRefs(raw)
    if (parsed.length === 0) {
      setManualErr(`Couldn't parse "${raw}". Try a form like "Romans 8:1" or "Ephesians 6:10-18".`)
      return
    }
    setRefs(curr => {
      const seen = new Set(curr.map(r => r.verseRef))
      const next = curr.slice()
      for (const p of parsed) {
        if (seen.has(p.verseRef)) continue
        next.push({ verseRef: p.verseRef, book: p.book, chapter: p.chapter, verse: p.verse })
        seen.add(p.verseRef)
      }
      return next
    })
    setManualRef('')
  }

  async function handleScan() {
    if (!text.trim()) return
    setScanning(true); setScanError('')
    try {
      const found = await scanTranscript(text, {
        title:  sharedBy ? `Encouragement from ${sharedBy}` : 'Encouragement',
        series: '',
        date:   date || todayIso(),
      })
      setRefs(found || [])
    } catch (e) {
      setScanError('Could not scan for references: ' + e.message)
    } finally {
      setScanning(false)
    }
  }

  function removeRef(i) {
    setRefs(r => r.filter((_, idx) => idx !== i))
  }

  async function handleGenerate() {
    if (!text.trim()) return
    setGenerating(true); setGenError('')
    try {
      const result = await generateEncouragementSummary(text)
      if (!result) { setGenError('Could not generate a summary — try again, or fill in the title and summary yourself.'); return }
      if (result.title) setTitle(result.title)
      if (result.summary) setSummary(result.summary)
      setIsLikelySermon(!!result.isLikelySermon)
    } finally {
      setGenerating(false)
    }
  }

  // Opens the preview (docs/FIX-ENCOURAGEMENT-UPLOAD.md §5) — the actual
  // insert/update happens in doSave, triggered from there.
  async function handleReview() {
    if (!text.trim() && !summary.trim()) return
    // Auto-scan if the moderator hasn't already done it and the text is
    // long enough - otherwise this encouragement won't get linked to any
    // verses, which defeats the whole point of the heart indicator.
    let refsToSave = refs
    if (refsToSave.length === 0 && text.trim().length > 30) {
      try {
        const found = await scanTranscript(text, {
          title:  sharedBy ? `Encouragement from ${sharedBy}` : 'Encouragement',
          series: '',
          date:   date || todayIso(),
        })
        refsToSave = found || []
      } catch (e) {
        console.warn('Auto-scan on save failed, falling back to local parser:', e.message)
      }
    }
    // Fallback: if Claude's scan returned nothing (or failed), parse refs
    // out of the text ourselves with a regex. Better to save some refs than
    // none - the verse-link heart depends on this.
    if (refsToSave.length === 0) {
      refsToSave = parseBibleRefs(text)
    }
    setRefs(refsToSave)
    // A summary hasn't been generated yet — don't block the preview, but
    // there's nothing short to show, so fall back to the raw text capped to
    // a sane length rather than showing nothing.
    if (!summary.trim()) setSummary(text.trim().slice(0, 400))
    setPreviewOpen(true)
  }

  async function doSave() {
    setSaving(true); setSaveError('')
    try {
      const startSeconds = parseTimestampToSeconds(startsAt.trim())
      const startMs = startSeconds != null ? startSeconds * 1000 : null
      const endMs = startMs != null ? startMs + Math.min(clipSeconds, MAX_CLIP_S) * 1000 : null
      const linkUrl = appendYoutubeTimestamp(youtubeUrl.trim(), startsAt.trim()) || null
      const finalSummary = summary.trim() || text.trim().slice(0, 400)
      // Only keep the original pasted text as a separate excerpt when it's
      // meaningfully longer than the summary itself — no point storing a
      // near-duplicate.
      const transcriptExcerpt = text.trim().length > finalSummary.length + 50 ? text.trim() : null
      const payload = {
        title:      title.trim() || null,
        text:       finalSummary,
        summary:    finalSummary,
        transcriptExcerpt,
        startMs,
        endMs,
        sharedBy:   sharedBy.trim() || null,
        eventDate:  date || null,
        linkUrl,
        linkLabel:  linkUrl ? 'Watch on YouTube' : null,
        refs,
        churchId,
      }
      const res = isEdit
        ? await updateEncouragement(existing.id, payload)
        : await addEncouragement(payload)
      if (res?.error) {
        setSaveError(typeof res.error === 'string' ? res.error : (res.error.message || 'Could not save encouragement.'))
        setPreviewOpen(false)
        return
      }
      setPreviewOpen(false)
      setSaved(true)
      setTimeout(() => {
        setSaved(false)
        if (!isEdit) {
          setTitle(''); setSharedBy(defaultSharedBy); setDate(todayIso()); setText(''); setSummary(''); setRefs([])
          setYoutubeUrl(''); setStartsAt(''); setClipLength(String(DEFAULT_CLIP_S)); setIsLikelySermon(false)
        }
        onSaved?.()
      }, 1500)
    } finally {
      setSaving(false)
    }
  }

  if (saved) return (
    <div style={{...s.wrap, textAlign:'center', paddingTop:60}}>
      <div style={{fontSize:40, marginBottom:16}}>✓</div>
      <div style={s.title}>{isEdit ? 'Changes saved!' : 'Encouragement saved!'}</div>
    </div>
  )

  const canSave = text.trim().length > 0 || summary.trim().length > 0
  const canScan = text.trim().length > 30 && !scanning
  const canGenerate = text.trim().length > 10 && !generating

  if (previewOpen) return (
    <EncouragementPreview
      title={title} summary={summary || text.trim().slice(0, 400)} refs={refs}
      youtubeUrl={youtubeUrl} startsAt={startsAt} clipSeconds={Math.min(clipSeconds, MAX_CLIP_S)}
      saving={saving} saveError={saveError}
      onBack={()=>setPreviewOpen(false)} onConfirm={doSave}
    />
  )

  return (
    <div style={s.wrap}>
      <div style={s.title}>{isEdit ? 'Edit encouragement' : 'Add an encouragement'}</div>
      <div style={s.sub}>{isEdit ? 'Update the details below — title, body, date, references or YouTube link.' : 'Share an encouragement, reflection, or transcript with the church.'}</div>

      <label style={s.label}>Title</label>
      <input style={s.input} placeholder="e.g. A word on hope"
        value={title} onChange={e=>setTitle(e.target.value)}/>

      <label style={s.label}>Shared by</label>
      <input style={s.input} placeholder="Who shared this encouragement?"
        value={sharedBy} onChange={e=>setSharedBy(e.target.value)}/>

      <label style={s.label}>Date</label>
      <input style={s.input} type="date" value={date} onChange={e=>setDate(e.target.value)}/>

      <label style={s.label}>Source text</label>
      <textarea
        style={{...s.textarea, minHeight:180}}
        placeholder="Paste the encouragement here — a short note, or a transcript excerpt to summarise"
        value={text}
        onChange={e=>setText(e.target.value)}
      />

      <button
        type="button"
        style={{...s.scanBtn, ...(canGenerate ? {} : s.disBtn)}}
        disabled={!canGenerate}
        onClick={handleGenerate}>
        {generating ? '✦ Generating…' : '✦ Generate summary & title'}
      </button>
      {genError && <div style={{...s.status, ...s.err}}>{genError}</div>}
      {isLikelySermon && (
        <div style={{...s.status, ...s.err, marginTop:10}}>
          This looks like part of the sermon, not a congregation encouragement. Consider switching to Sermon mode above instead.
        </div>
      )}

      <label style={s.label}>Summary (shown in Community)</label>
      <textarea
        style={{...s.textarea, minHeight:90}}
        placeholder="A short, warm summary — generate one above, or write it yourself"
        value={summary}
        onChange={e=>setSummary(e.target.value)}
        maxLength={400}
      />

      <label style={s.label}>Bible verses</label>
      <button
        type="button"
        style={{...s.scanBtn, ...(canScan ? {} : s.disBtn)}}
        disabled={!canScan}
        onClick={handleScan}>
        {scanning ? '✦ Scanning…' : '✦ Scan encouragement text for Bible references'}
      </button>
      {scanError && <div style={{...s.status, ...s.err}}>{scanError}</div>}
      {refs.length > 0 && (
        <div style={{display:'flex', flexWrap:'wrap', gap:6, marginTop:10}}>
          {refs.map((r, i) => (
            <span key={i} style={s.refPill}>
              {r.verseRef}
              <button type="button" style={s.refX} onClick={()=>removeRef(i)} aria-label="Remove">✕</button>
            </span>
          ))}
        </div>
      )}

      {/* Manual Bible-ref entry. Use the same parseBibleRefs regex as
          the auto-scan path so "Romans 8:1", "1 Cor 12:1-11", and book
          aliases (Psalm vs Psalms) all work the same way. */}
      <div style={{display:'flex', gap:6, marginTop:10}}>
        <input
          style={{...s.input, flex:1}}
          placeholder='Add a reference, e.g. "Romans 8:1" or "Eph 6:10-18"'
          value={manualRef}
          onChange={e=>setManualRef(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addManualRef() } }}/>
        <button type="button"
          onClick={addManualRef}
          disabled={!manualRef.trim()}
          style={{
            background:'rgba(232,200,122,0.12)', color:'var(--gold-ink)',
            border:'1px solid rgba(232,200,122,0.3)', borderRadius:10,
            padding:'0 16px', fontSize:13, fontWeight:700,
            cursor: manualRef.trim() ? 'pointer' : 'default',
            opacity: manualRef.trim() ? 1 : 0.5,
            fontFamily:"'Plus Jakarta Sans',sans-serif", whiteSpace:'nowrap',
          }}>
          Add
        </button>
      </div>
      {manualErr && <div style={{...s.status, ...s.err, marginTop:8}}>{manualErr}</div>}

      <label style={s.label}>YouTube URL</label>
      <input style={s.input} type="url" placeholder="Optional — whole video if left as-is"
        value={youtubeUrl} onChange={e=>setYoutubeUrl(e.target.value)}/>

      {youtubeUrl.trim() && (
        <>
          <div style={{display:'flex', gap:10}}>
            <div style={{flex:1}}>
              <label style={s.label}>Starts at</label>
              <input style={s.input} placeholder="e.g. 12:34, where the moment begins"
                value={startsAt} onChange={e=>setStartsAt(e.target.value)}/>
            </div>
            <div style={{flex:1}}>
              <label style={s.label}>Clip length (seconds)</label>
              <input style={s.input} type="number" min={1} max={MAX_CLIP_S}
                value={clipLength} onChange={e=>setClipLength(e.target.value)}/>
            </div>
          </div>
          {clipTooLong && (
            <div style={{...s.status, ...s.err}}>An encouragement clip can't be longer than 5 minutes (300s).</div>
          )}
        </>
      )}

      {saveError && <div style={{...s.status, ...s.err}}>{saveError}</div>}

      <button
        style={{...s.saveBtn, ...(canSave && !clipTooLong ? {} : s.disBtn)}}
        disabled={!canSave || clipTooLong}
        onClick={handleReview}>
        Review &amp; save →
      </button>
    </div>
  )
}

// Preview before saving (docs/FIX-ENCOURAGEMENT-UPLOAD.md §5) — title,
// summary, refs, and a play button for the clip window, so a bad save never
// reaches Community unseen.
function EncouragementPreview({ title, summary, refs, youtubeUrl, startsAt, clipSeconds, saving, saveError, onBack, onConfirm }) {
  const startSeconds = parseTimestampToSeconds(startsAt?.trim())
  const videoId = youtubeIdFromUrl(youtubeUrl)
  const embedUrl = videoId && startSeconds != null
    ? `https://www.youtube.com/embed/${videoId}?start=${startSeconds}&end=${startSeconds + clipSeconds}&autoplay=0`
    : null

  return (
    <div style={s.wrap}>
      <div style={s.title}>Preview</div>
      <div style={s.sub}>Check this before it reaches Community.</div>

      <div style={{background:'var(--white)', border:'1px solid var(--border)', borderRadius:14, padding:16, marginTop:4}}>
        {embedUrl ? (
          <div style={{position:'relative', width:'100%', paddingTop:'56.25%', borderRadius:10, overflow:'hidden', marginBottom:14, background:'#000'}}>
            <iframe
              src={embedUrl} title="Encouragement clip preview"
              style={{position:'absolute', inset:0, width:'100%', height:'100%', border:'none'}}
              allow="autoplay; encrypted-media" allowFullScreen/>
          </div>
        ) : youtubeUrl?.trim() ? (
          <div style={{...s.status, ...s.info, marginBottom:14}}>No valid start time — the clip will play from the start of the video.</div>
        ) : null}
        <div style={{fontSize:17, fontWeight:800, color:'var(--ink)', marginBottom:6}}>{title || 'Untitled encouragement'}</div>
        <div style={{fontSize:14, color:'var(--ink)', lineHeight:1.5, marginBottom:10}}>{summary}</div>
        {refs.length > 0 && (
          <div style={{display:'flex', flexWrap:'wrap', gap:6}}>
            {refs.map((r, i) => <span key={i} style={s.refPill}>{r.verseRef}</span>)}
          </div>
        )}
        {embedUrl && <div style={{fontSize:12, color:'var(--stone)', marginTop:10}}>Clip: {startsAt} for {clipSeconds}s</div>}
      </div>

      {saveError && <div style={{...s.status, ...s.err}}>{saveError}</div>}

      <div style={{display:'flex', gap:10, marginTop:14}}>
        <button type="button" onClick={onBack} disabled={saving}
          style={{flex:1, background:'var(--white)', border:'1px solid var(--border)', borderRadius:10, padding:'13px', fontSize:14, fontWeight:700, color:'var(--ink)', cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
          ← Back
        </button>
        <button type="button" onClick={onConfirm} disabled={saving}
          style={{...s.saveBtn, flex:1, marginTop:0, ...(saving ? s.disBtn : {})}}>
          {saving ? 'Saving…' : 'Looks good, save'}
        </button>
      </div>
    </div>
  )
}
